import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Leaf } from "lucide-react";
import { LOGO_URL, BUSINESS } from "../constants";

const heroImage =
  "https://images.unsplash.com/photo-1771678040857-51d00a11be7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG1vZGVybiUyMGludGVyaW9yJTIwc3BhY2V8ZW58MHx8fHwxNzc3OTkwNzM0fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-screen pt-28 pb-20 overflow-hidden bg-[#F8FAFC]"
      data-testid="hero-section"
    >
      {/* Background grid lines */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              "linear-gradient(#0F2B5B 1px, transparent 1px), linear-gradient(90deg, #0F2B5B 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left content */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 text-xs font-medium text-[#0F2B5B] mb-8"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#2E9E6E] animate-pulse" />
            Nettoyage professionnel · {BUSINESS.zone}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-semibold text-5xl md:text-6xl lg:text-[4.5rem] leading-[0.95] tracking-tight text-[#0F2B5B]"
            data-testid="hero-title"
          >
            Des espaces
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">impeccables</span>
              <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#2E9E6E]/25 -z-0" />
            </span>
            ,
            <br />
            une image qui inspire.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 text-lg text-slate-600 leading-relaxed max-w-xl"
            data-testid="hero-subtitle"
          >
            Néma Services accompagne entreprises, commerces, copropriétés et
            sites industriels avec des prestations de nettoyage sur mesure —
            fiables, réactives et éco-responsables.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#devis"
              className="group inline-flex items-center justify-center gap-2 bg-[#0F2B5B] hover:bg-[#153a7a] text-white px-7 py-4 text-sm font-medium tracking-wide transition-colors"
              data-testid="hero-cta-primary"
            >
              Obtenir un devis gratuit
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#services"
              className="inline-flex items-center justify-center gap-2 border border-[#0F2B5B]/20 hover:border-[#0F2B5B] text-[#0F2B5B] px-7 py-4 text-sm font-medium transition-colors bg-white"
              data-testid="hero-cta-secondary"
            >
              Découvrir nos prestations
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 flex flex-wrap gap-6 text-sm text-slate-600"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#2E9E6E]" strokeWidth={1.75} />
              Équipe assurée
            </div>
            <div className="flex items-center gap-2">
              <Leaf className="w-4 h-4 text-[#2E9E6E]" strokeWidth={1.75} />
              Produits éco-responsables
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#2E9E6E]" strokeWidth={1.75} />
              Contrôle qualité systématique
            </div>
          </motion.div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden border border-slate-200">
              <img
                src={heroImage}
                alt="Espace intérieur moderne et immaculé"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2B5B]/40 via-transparent to-transparent" />
            </div>

            {/* Floating card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="absolute -bottom-6 -left-6 md:-left-10 bg-white border border-slate-200 p-5 max-w-[240px] shadow-xl"
              data-testid="hero-floating-card"
            >
              <div className="flex items-center gap-3 mb-3">
                <img src={LOGO_URL} alt="" className="w-10 h-10 object-contain" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#2E9E6E] font-medium">
                    Satisfaction
                  </div>
                  <div className="font-display text-2xl font-semibold text-[#0F2B5B] leading-none">
                    98%
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-600 leading-relaxed">
                Clients professionnels qui nous recommandent après la première
                intervention.
              </div>
            </motion.div>

            {/* Top-right badge */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="absolute -top-4 -right-4 bg-[#2E9E6E] text-white px-4 py-3 text-xs font-medium"
            >
              <div className="uppercase tracking-[0.2em] opacity-80">Depuis</div>
              <div className="font-display text-2xl leading-none mt-1">+10 ans</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
